import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import HelpIcon from '@mui/icons-material/Help'
import LanguageIcon from '@mui/icons-material/Language'
import MenuOpenRoundedIcon from '@mui/icons-material/MenuOpenRounded'
import SwapHorizontalCircleIcon from '@mui/icons-material/SwapHorizontalCircle'
import AppBar from '@mui/material/AppBar'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import theme from 'assets/theme'
import { KNUserIcon } from 'components/KNIcon/KNIcon'
import { KNUnitsIcon } from 'components/KNIcon/KNMaterialIcon'
import KNMenu from 'components/KNMenu/KNMenu'
import KNMenuItem from 'components/KNMenu/KNMenuItem'
import useMenuState from 'components/KNMenu/useMenuState'
import type { Company } from 'context/authentication/Company.types'
import { GuestUserContext } from 'context/authentication/GuestUserContext'
import { getSelectedCompany, getStartPage, setSelectedCompany } from 'context/authentication/User.helpers'
import { UserContext } from 'context/authentication/UserContext'
import { useMenuContext } from 'context/menu/MenuContext'
import { analyticsEvent } from 'global/helpers/analytics'
import { useBreakpoints } from 'global/helpers/breakpoints'
import { getLogo } from 'global/helpers/getLogo'
import { getLanguages } from 'global/helpers/languages'
import i18n from 'i18n'
import { getUserMenuOptions, type SectionOption } from 'modules/DrawerMenu/DrawerMenu.helpers'
import React, { Fragment, type ReactElement, useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink as RouterNavLink } from 'react-router-dom'
import { MeasurementsSystem } from 'screens/UserManager/UserManager.types'

export const NavButton = styled(Button)(({ theme }) => ({ // NOSONAR - typescript:S4325 this cast is necessary for "component" prop to be recognized
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  color: '#fff',
  fontSize: theme.typography.size.md,
})) as typeof Button

const TopNavigation = (): ReactElement | null => {
  const { isMedium } = useBreakpoints()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { t } = useTranslation()
  const userMenuOptions = getUserMenuOptions()

  // Context
  const { user, logoutUser } = useContext(UserContext)
  const [guestUserState, guestUserDispatch] = useContext(GuestUserContext)
  const [menuState, dispatch] = useMenuContext()
  const { menuOpen, menuExtended } = menuState.menuContext
  const showCompanySelector = user?.companies && user.companies.length > 1
  // Dropdown
  const [companyMenuAnchorEl, setCompanyMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null)
  const companyMenuOpen = Boolean(companyMenuAnchorEl)
  const userMenuOpen = Boolean(userMenuAnchorEl)
  const [currentLanguage, setCurrentLanguage] = useState<string>()
  const {
    anchorEl: languagesAnchorEl,
    open: languagesOpen,
    handleClick: handleLanguagesClick,
    handleClose: handleLanguagesClose,
  } = useMenuState()
  const languages = getLanguages()
  const {
    anchorEl: unitsAnchorEl,
    open: unitsOpen,
    handleClick: handleUnitsClick,
    handleClose: handleUnitsClose,
  } = useMenuState()

  const isWeblink = globalThis.location.href.includes('/wl/')

  const handleClickCompanyMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setCompanyMenuAnchorEl(event.currentTarget)
  }
  const handleClickUserMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setUserMenuAnchorEl(event.currentTarget)
  }
  const handleClose = useCallback((): void => {
    if (companyMenuAnchorEl) {
      setCompanyMenuAnchorEl(null)
    } else if (userMenuAnchorEl) {
      setUserMenuAnchorEl(null)
    }
  }, [companyMenuAnchorEl, userMenuAnchorEl])

  const handleSelectCompany = useCallback((company: Company): void => {
    setSelectedCompany(company)
    analyticsEvent('polestar_selected_company_changed')
    globalThis.location.assign(getStartPage())
  }, [])

  const knCompanyFilter = () => {
    const companies = user?.companies
    if (companies && companies.length > 0) {
      const index = companies.findIndex(company => company.displayName.toLowerCase() === 'global admin')
      if (index && index > -1) {
        const [admin] = companies.splice(index, 1)
        companies.unshift(admin!)
      }
    }
    return companies
  }

  const getCompanySelector = (sectionOption): ReactElement => {
    return (<Fragment key={sectionOption.name}>
      <NavButton
        id="company-button"
        data-guide="menu-company"
        variant="text"
        endIcon={<SwapHorizontalCircleIcon />}
        aria-controls={companyMenuOpen ? 'company-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={companyMenuOpen || undefined}
        onClick={handleClickCompanyMenu}
      >
        {getSelectedCompany()?.displayName}
      </NavButton>
      <Menu
        id="company-menu"
        anchorEl={companyMenuAnchorEl}
        open={companyMenuOpen}
        onClose={handleClose}
        slotProps={{
          list: {
            'aria-labelledby': 'company-button',
          },
        }}
      >
        {knCompanyFilter()?.map((company) => (
          <MenuItem
            key={company.displayName}
            onClick={(): void => handleSelectCompany(company)}
            data-test={`drawer-menu-companySelector-${company.displayName}`}
          >
            {company.displayName}
          </MenuItem>
        ))}
      </Menu>
    </Fragment>)
  }

  const getUnitsSection = () => {
    return (
      <>
        <NavButton
          variant="text"
          endIcon={<KNUnitsIcon />}
          onClick={handleUnitsClick}
        >
          {t(`screens.shared.profile.${guestUserState.measurementsSystem}`)}
        </NavButton>
        <KNMenu
          open={unitsOpen}
          onClose={handleUnitsClose}
          anchorEl={unitsAnchorEl}
          rightAligned
          bottomAligned
        >
          <KNMenuItem
            key="metric"
            label={t(`screens.shared.profile.METRIC`)}
            onClick={() => handleUnitClick(MeasurementsSystem.METRIC)}
          />
          <KNMenuItem
            key="imperial"
            label={t(`screens.shared.profile.IMPERIAL`)}
            onClick={() => handleUnitClick(MeasurementsSystem.IMPERIAL)}
          />
        </KNMenu>
      </>
    )
  }

  const handleUnitClick = (measurementsSystem: MeasurementsSystem): void => {
    guestUserDispatch({ type: 'setMeasurementsSystem', payload: measurementsSystem })
  }

  useEffect(() => {
    const newLanguage = languages.find((language) => language.languageCode === i18n.language)
    setCurrentLanguage(newLanguage?.label ?? '')
  }, [i18n.language])

  const handleLanguageClick = async (languageCode: string): Promise<void> => {
    await i18n.changeLanguage(languageCode)
    analyticsEvent('polestar_change_language', [languageCode])
  }

  const getLanguageSection = () => {
    return (
      <>
        <NavButton
          variant="text"
          endIcon={<LanguageIcon />}
          onClick={handleLanguagesClick}
        >
          {currentLanguage}
        </NavButton>
        <KNMenu
          open={languagesOpen}
          onClose={handleLanguagesClose}
          anchorEl={languagesAnchorEl}
          rightAligned
          bottomAligned
        >
          {languages.map((language) => (
            <KNMenuItem
              key={language.languageCode}
              label={language.label}
              onClick={async (): Promise<void> => await handleLanguageClick(language.languageCode)}
            />
          ))}
        </KNMenu>
      </>
    )
  }

  const getUserSection = (sectionOption) => {
    return user && (<Fragment key={sectionOption.name}>
      <NavButton
        id="user-button"
        data-guide="menu-profile"
        variant="text"
        endIcon={<AccountCircleIcon />}
        aria-controls={userMenuOpen ? 'user-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={userMenuOpen ? 'true' : undefined}
        onClick={handleClickUserMenu}
      >
        {user.displayName}
      </NavButton>
      <Menu
        id="user-menu"
        anchorEl={userMenuAnchorEl}
        open={userMenuOpen}
        onClose={handleClose}
        slotProps={{
          list: {
            'aria-labelledby': 'user-button',
          },
        }}
      >
        <MenuItem
          component={RouterNavLink}
          to="/profile"
          onClick={handleClose}
          data-test="drawer-menu-navigate-profile"
        >
          {t('drawer_menu.show_my_profile')}
        </MenuItem>
        <MenuItem
          onClick={(): void => {
            logoutUser()
            analyticsEvent('polestar_logout')
          }}
          data-test="logout-button"
        >
          {t('drawer_menu.logout')}
        </MenuItem>
      </Menu>
    </Fragment>)
  }

  const getHelpSection = () => {
    return (<NavButton
      key="help-and-info"
      component={RouterNavLink}
      to="/help-and-info"
      endIcon={<HelpIcon />}
    >
      {t('drawer_menu.help_and_info')}
    </NavButton>)

  }
  const getLoginSection = () => {
    return <NavButton
      key="login_button"
      component={RouterNavLink}
      to="/login"
      endIcon={<KNUserIcon />}
    >
      {t('general.go_to_login')}
    </NavButton>
  }

  const getSection = (sectionOption: SectionOption) => {
    //User
    if (user) {
      if (sectionOption.name === 'user') {
        return getUserSection(sectionOption)
      } else if (sectionOption.name === 'help-and-info') {
        return getHelpSection()
      } else if (sectionOption.label === 'company_name') {
        //Company
        return showCompanySelector ? (
          getCompanySelector(sectionOption)
        ) : (
          <Typography
            color="white"
            sx={{
              fontSize: '0.875rem',
            }}
          >
            {getSelectedCompany()?.displayName}
          </Typography>
        )
      }
    } else if (sectionOption.label === 'units') {
      return !isWeblink && getUnitsSection()
    } else if (sectionOption.label === 'login_button') {
      return isWeblink && !isMobile && getLoginSection()
    } else if (sectionOption.label === 'language') {
      return getLanguageSection()
    }
  }

  const handleDrawerMenuOpen = (): void => {
    dispatch({
      type: 'setMenuProps',
      payload: {
        menuOpen: !menuOpen,
        menuExtended: menuExtended,
      },
    })
    analyticsEvent('polestar_top_navigation_menu_toggled')
  }

  return (
    <AppBar elevation={0}>
      <Toolbar variant="dense">
        {!isMedium && (
          <>
            <Stack spacing={2} direction="row" alignItems="center" sx={{ flexGrow: 1 }}>
              {getLogo('logo', 'auto', '2rem', 'light.light')}
              <Typography noWrap color="light.light">
                {t('drawer_menu.carrier_solution')}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center">
              {userMenuOptions.map((section) => (
                <Stack direction="row" alignItems="center" justifyContent="end" key={section.sectionName}>
                  {section.sectionOptions.map((sectionOption) => (
                    <Fragment key={sectionOption.name}>
                      {getSection(sectionOption)}
                    </Fragment>
                  ))}
                </Stack>
              ))}
            </Stack>
          </>
        )}

        {/* MOBILE MENU */}
        {isMedium && (
          <>
            <Stack direction="row" spacing={2} sx={{ height: '32px', marginRight: 'auto', marginLeft: '1em' }}>
              {getLogo('symbol', '32px', '32px', 'light.light')}
              <Typography noWrap paddingTop="5px" color="light.light">{t('drawer_menu.carrier_solution')}</Typography>
            </Stack>

            {user ? <IconButton onClick={(): void => handleDrawerMenuOpen()} data-test={'drawer-menu-openButton'}>
              <MenuOpenRoundedIcon sx={{ color: 'light.light' }} />
            </IconButton> : <Stack direction="row" alignItems="center">{userMenuOptions.map((section) => (
              <Stack direction="row" alignItems="center" justifyContent="end" spacing={2} key={section.sectionName}>
                {section.sectionOptions.map((sectionOption) => (
                  <Fragment key={sectionOption.name}>
                    {getSection(sectionOption)}
                  </Fragment>
                ))}
              </Stack>
            ))}
            </Stack>}
          </>
        )}
      </Toolbar>
    </AppBar>
  )
}

export default TopNavigation
